import { ReactNode } from 'react';

type Primitive = string | number | boolean;

export enum PageLayoutWidth {
  Small = 'small',
  Medium = 'medium',
  Large = 'large',
  XLarge = 'xlarge',
}

export enum PageLayoutContentMainAreaAriaRole {
  Main = 'main',
  Region = 'region',
}

export interface OnRenderHeroBannerOptions {
  readonly hasHeader: boolean;
  readonly noSpacing: boolean;
  readonly width?: PageLayoutWidth;
}

export enum PageLayoutContentSidebarPosition {
  RightOfContent = 'RightOfContent',
  LeftOfContent = 'LeftOfContent',
}

interface OnPageLayoutContentRenderedParams {
  readonly isSidebarVisible: boolean;
  readonly isSidebarInPanel: boolean;
  readonly width: PageLayoutWidth | undefined;
}

interface BasePageLayoutProps {
  /*
   * Aria-label for the button to close the right sidebar pane - used in narrow breakpoints
   */
  readonly closeContentSidebarAriaLabel?: string;

  /*
   * Optional prop to collapse left sidebar regardless of screen size.
   */
  readonly collapseLeftSidebar?: boolean;

  /*
   * Optional prop that indicates custom open and close functionality for the panel containing sidebar contents when in narrow breakpoints.
   * Defaults to false.
   */
  readonly isPanelExternallyControlled?: boolean;

  /*
   * Allows body not to be scrollable.
   */
  readonly noScroll?: boolean;

  /*
   * Allows content column to occupy more of the available space.
   */
  readonly noSpacing?: boolean;

  /**
   * Optional prop to removes the padding for the scrollbar gutter - used when a scrollbar will not take up space in the margin.
   */
  readonly supressNoScrollRightPadding?: boolean;

  /**
   * Optional prop used to reset scroll and focus change. Reset triggered when this changes.
   */
  readonly resetState?: Primitive;

  /*
   * Aria-label for the button to show the right sidebar pane - used in narrow breakpoints
   */
  readonly showContentSidebarAriaLabel?: string;

  /*
   * Optional prop to force content column to have no top padding.
   */
  readonly suppressTopPadding?: boolean;

  /**
   * Optional prop to apply a full width layout to page.
   */
  readonly fullWidthLayout?: boolean;

  /**
   * Optional prop to pass a callback invoked when PageLayoutContent's sidebar is rendered as a column in the content area or in a panel for narrow widths.
   */
  onPageLayoutContentRendered?(params: OnPageLayoutContentRenderedParams): void;

  /**
   * Optional render prop for header content. Will render at the top of the page.
   */
  onRenderHeader?(width: PageLayoutWidth | undefined): ReactNode;

  /**
   * Optional render prop for hero banner. Will render at the top of the page, wrapping children of PageLayoutContent.
   */
  onRenderHeroBanner?(props: OnRenderHeroBannerOptions): ReactNode;

  /**
   * Optional render prop for collapsible panel. Will render a collapsible panel at the right of the page at full height but clearing the PageLayoutHeader, PageLayoutContentSubheader and PageLayoutBanner.
   */
  onRenderCollapsiblePanel?(width: PageLayoutWidth | undefined): ReactNode;

  /**
   * Optional render prop for footer content. Will render at the bottom of the page.
   */
  onRenderFooter?(): ReactNode;

  /**
   * Optional render prop for global banner content. Will render below header.
   */
  onRenderBanner?(width: PageLayoutWidth | undefined): ReactNode;

  /**
   * Optional render prop for header pivot. Will render at the top when screen width is small.
   */
  onRenderSubHeader?(): ReactNode;

  /**
   * Optional render prop for near sidebar content. Will render on the left side for ltr, and right for rtl.
   */
  onRenderLeftSidebar?(): ReactNode;
}

interface PageLayoutPropsWithControlledContentSidebarPanel extends BasePageLayoutProps {
  /*
   * Prop that indicates custom open and close functionality for the panel containing sidebar contents when in narrow breakpoints.
   */
  readonly isPanelExternallyControlled: true;

  /*
   * Prop that determines whether the panel containing sidebar contents in narrow breakpoints is open or close.
   */
  readonly isPanelOpen: boolean;

  /*
   * Optional prop that shows toggle button for panel containing sidebar content on specified widths.
   */
  readonly showContentPanelButtonForWidths?: PageLayoutWidth[];

  /*
   * Callback to customize closing the panel containing sidebar contents when in narrow breakpoints.
   * Takes the place of the default close panel functionality provided by PageLayout.
   */
  closePanel(): void;

  /*
   * Callback to customize opening the panel containing sidebar contents when in narrow breakpoints.
   * Takes the place of the default open panel functionality provided by PageLayout.
   */
  openPanel(): void;
}

export type PageLayoutProps = BasePageLayoutProps | PageLayoutPropsWithControlledContentSidebarPanel;

export const isExternallyControlledContentSidebarPanel = (
  props: PageLayoutProps
): props is PageLayoutPropsWithControlledContentSidebarPanel => !!props.isPanelExternallyControlled;

export interface PageLayoutContentProps {
  /*
   * Optional prop to collapse the sidebar regardless of screen size.
   */
  readonly collapseSidebar?: boolean;

  /*
   * Prop to set the aria role of content area.
   * If PageLayoutConent is going to be rendered in a hosted app (e.g. an integration) choose PageLayoutContentAriaRole.Region. Choose PageLayoutContentAriaRole.Main otherwise.
   */
  readonly mainAreaAriaRole: PageLayoutContentMainAreaAriaRole;

  /*
   * Optional prop to enable sticky scrolling on right sidebar.
   */
  readonly stickyScrollSidebar?: boolean;

  /*
   * Optional prop to use wide side bar
   */
  readonly useWiderSidebar?: boolean;

  /*
   * Optional prop to place the sidebar on the left side. Defaults to PageLayoutContentSidebarPosition.RightOfContent which renders sidebar on the right of the content.
   */
  readonly sidebarPosition?: PageLayoutContentSidebarPosition;

  /**
   * Optional render prop for far sidebar content. Will render on the right side for ltr, and left for rtl.
   */
  onRenderSidebar?(width?: PageLayoutWidth): ReactNode;
}
